// Everflow click-tracking integration.
//
// initEverflow() runs once at boot: load the EF SDK, configure organic
// attribution, fire EF.click with the funnel arm (from GrowthBook, bounded by
// EF_GB_TIMEOUT_MS) as sub7. URL params are snapshot synchronously because
// react-router strips the query string on the first navigation. The resulting
// transaction id lands in the `transaction_id` cookie for signup/login;
// `?clickid=` (RedTrack) visits clear EF cookies and skip the click instead.
//
// Two repair flows fire a REPLACEMENT click carrying the real arm and rebind
// the visit to it (conversions follow it; the old click is abandoned):
//  - GB timeout: the initial click went out with sub7 `unknown` — re-click
//    once GB resolves later in the pageview.
//  - Foreign click (reused `?ef_transaction_id`, or a repeat visit whose new
//    click the EF witness flag suppressed): ask the backend for its
//    attribution and replace only an empty / n-a / unknown sub7.

import { growthbook, gbReady } from '../lib/growthbookClient'
import {
  PAYWALL_NAV_CONFIG_FLAG,
  DEFAULT_NAV_CONFIG,
  resolveNavConfig,
  resolveAuthType,
  AUTH_TYPE_UNKNOWN,
} from './authType'

const BASE_URL = process.env.REACT_APP_EVERFLOW_BASE_URL
const ORGANIC_OFFER_ID = process.env.REACT_APP_EVERFLOW_ORGANIC_OFFER_ID
const ORGANIC_PARTNER_ID = process.env.REACT_APP_EVERFLOW_ORGANIC_PARTNER_ID
const SWIPEY_BASE_URL = process.env.REACT_APP_SWIPEY_BASE_URL
const API_KEY = process.env.REACT_APP_API_KEY

// How long the click waits for GrowthBook before firing with sub7 `unknown`.
const EF_GB_TIMEOUT_MS = 1500

// sessionStorage: replacement click id authored this pageview (per-tab, and
// cleared at boot so a previous pageview's id can't shadow a newer click).
const REPLACEMENT_TID_KEY = 'csf_ef_replacement_tid'

// localStorage, single slot (the cookie carries one active transaction): last
// id whose sub7 is confirmed usable — suppresses repeat lookups.
const SUB7_OK_KEY = 'csf_ef_sub7_ok_tid'

// Guards the id interpolated into the lookup URL; format-agnostic on purpose.
const EF_TRANSACTION_ID_RE = /^[a-zA-Z0-9]{16,64}$/

// sub7 is absent on purpose — it carries the auth type, not a URL value.
const SUB_KEYS = [
  'sub1',
  'sub2',
  'sub3',
  'sub4',
  'sub5',
  'sub6',
  'sub8',
  'sub9',
  'sub10',
]

const CLICK_PARAM_KEYS = [
  'cmpid',
  'pubid',
  'source_id',
  ...SUB_KEYS,
  'ef_transaction_id',
]

const pickSubs = (source) =>
  SUB_KEYS.reduce((acc, key) => {
    acc[key] = source[key] || ''
    return acc
  }, {})

const snapshotClickParams = (urlParams) =>
  CLICK_PARAM_KEYS.reduce((acc, key) => {
    acc[key] = urlParams.get(key) || ''
    return acc
  }, {})

// Mirrors the SDK's organic click defaults, snapshot at boot before the query
// string is stripped, so an organic replacement matches the original click.
const snapshotOrganicDefaults = (urlParams) => {
  let source = ''
  const fbclid = urlParams.get('fbclid')
  if (fbclid) source = fbclid.slice(0, 3) === 'PAA' ? 'Instagram' : 'Facebook'
  if (urlParams.get('gclid')) source = 'Google'
  if (urlParams.get('ttclid')) source = 'Tiktok'
  if (urlParams.get('sccid')) source = 'Snapchat'
  if (urlParams.get('MSCLKID') || urlParams.get('msclkid')) source = 'Microsoft'
  if (urlParams.get('OutbrainClickId')) source = 'Outbrain'
  if (urlParams.get('TCLID')) source = 'Taboola'

  return {
    sub1: urlParams.get('sub1') || source,
    sub2: urlParams.get('sub2') || document.referrer,
    sub3: urlParams.get('sub3') || '/' + window.location.pathname.split('/')[1],
    sub4: urlParams.get('sub4') || window.location.pathname,
    sub5: urlParams.get('sub5') || window.location.search,
    source_id: urlParams.get('source_id') || 'organic',
  }
}

// Un-timed arm resolution shared by the initial click and both repair flows;
// `unknown` on GB failure. Never rejects.
let authTypeEventualPromise = null
const resolveAuthTypeEventually = () => {
  if (!authTypeEventualPromise) {
    authTypeEventualPromise = gbReady
      .then((outcome) => {
        if (outcome?.success === false) return AUTH_TYPE_UNKNOWN
        const liveConfig = growthbook.getFeatureValue(
          PAYWALL_NAV_CONFIG_FLAG,
          DEFAULT_NAV_CONFIG,
        )
        return resolveAuthType(resolveNavConfig(liveConfig))
      })
      .catch((e) => {
        console.warn('[everflow] auth type resolution failed:', e)
        return AUTH_TYPE_UNKNOWN
      })
  }
  return authTypeEventualPromise
}

// Arm for the initial click — `unknown` when GB is slower than the timeout;
// the click must fire regardless.
const resolveAuthTypeForClick = () =>
  Promise.race([
    resolveAuthTypeEventually(),
    new Promise((resolve) =>
      setTimeout(() => resolve(AUTH_TYPE_UNKNOWN), EF_GB_TIMEOUT_MS),
    ),
  ])

const writeTransactionCookie = (transactionId) => {
  document.cookie = `transaction_id=${transactionId}; path=/; max-age=2592000; SameSite=Lax`
}

const clearEfCookies = () => {
  document.cookie = 'transaction_id=; path=/; max-age=0; SameSite=Lax'
  document.cookie = 'ef_transaction_id=; path=/; max-age=0; SameSite=Lax'
}

const rememberReplacementTid = (transactionId) => {
  try {
    sessionStorage.setItem(REPLACEMENT_TID_KEY, transactionId)
  } catch (e) {}
}

const clearReplacementTid = () => {
  try {
    sessionStorage.removeItem(REPLACEMENT_TID_KEY)
  } catch (e) {}
}

const markSub7Known = (transactionId) => {
  try {
    localStorage.setItem(SUB7_OK_KEY, transactionId)
  } catch (e) {}
}

const isSub7Known = (transactionId) => {
  try {
    return localStorage.getItem(SUB7_OK_KEY) === transactionId
  } catch (e) {
    return false
  }
}

// Unusable sub7: empty (reported as N/A), the panel literal n/a, and our own
// GB-timeout fallback.
const needsSub7Repair = (sub7) => {
  if (sub7 == null) return true
  const value = String(sub7).trim().toLowerCase()
  return value === '' || value === 'n/a' || value === AUTH_TYPE_UNKNOWN
}

// Backend proxy of the Everflow reporting API (the network API key cannot ship
// in this bundle). Returns the click info or null; never rejects.
const fetchClickInfo = async (transactionId) => {
  if (!SWIPEY_BASE_URL || !EF_TRANSACTION_ID_RE.test(transactionId)) return null

  try {
    const response = await fetch(
      `${SWIPEY_BASE_URL}/api/v1/marketing/everflow-click/${transactionId}`,
      { method: 'GET', headers: { 'x-api-key': API_KEY } },
    )
    if (!response.ok) return null
    const data = await response.json()
    return data?.found ? data : null
  } catch (err) {
    console.warn('[everflow] click info fetch failed:', err)
    return null
  }
}

// Records a fresh click carrying the real sub7 and rebinds the visit to it.
// `transaction_id` is never sent — the SDK would match the existing click
// instead of recording the new one.
const fireReplacementClick = (clickParams, authType) => {
  if (!clickParams || typeof window.EF === 'undefined') {
    return Promise.resolve(null)
  }

  return Promise.resolve()
    .then(() => window.EF.click({ ...clickParams, sub7: authType }))
    .then((transactionId) => {
      if (!transactionId) return null
      writeTransactionCookie(transactionId)
      rememberReplacementTid(transactionId)
      markSub7Known(transactionId)
      return transactionId
    })
    .catch((err) => {
      console.warn('[everflow] replacement click failed:', err)
      return null
    })
}

// Mirrors what the initial click sent. Organic ids go explicitly — the SDK's
// own organic fallback is single-shot (ef_witness) and would silently no-op.
const buildTimeoutReplacementParams = (params, organicDefaults) => {
  if (params.cmpid) {
    return {
      offer_id: params.cmpid,
      affiliate_id: params.pubid,
      source_id: params.source_id,
      ...pickSubs(params),
    }
  }

  if (!ORGANIC_OFFER_ID || !ORGANIC_PARTNER_ID) return null

  return {
    offer_id: Number(ORGANIC_OFFER_ID),
    affiliate_id: Number(ORGANIC_PARTNER_ID),
    ...organicDefaults,
    organic: 1,
  }
}

// Replace only when the looked-up sub7 is unusable AND GB resolved a real arm;
// the click's own subs are reused so only sub7 changes.
const repairForeignClick = async (transactionId) => {
  try {
    if (isSub7Known(transactionId)) return

    const authType = await resolveAuthTypeEventually()
    if (authType === AUTH_TYPE_UNKNOWN) return

    const info = await fetchClickInfo(transactionId)
    if (!info) return

    if (!needsSub7Repair(info.sub7)) {
      markSub7Known(transactionId)
      return
    }

    if (info.networkOfferId == null || info.networkAffiliateId == null) return

    await fireReplacementClick(
      {
        offer_id: info.networkOfferId,
        affiliate_id: info.networkAffiliateId,
        source_id: info.sourceId || '',
        ...pickSubs(info),
        ...(Number(ORGANIC_OFFER_ID) === info.networkOfferId
          ? { organic: 1 }
          : {}),
      },
      authType,
    )
  } catch (err) {
    console.warn('[everflow] click repair failed:', err)
  }
}

// Picks the repair path after the initial EF.click settles, so a repair's
// cookie write can never be overwritten by the initial click resolving late.
const followUpOnInitialClick = (
  transactionId,
  authType,
  params,
  organicDefaults,
) => {
  const urlTid = params.ef_transaction_id
  // The SDK echoes the id back when it matched an existing click, so anything
  // different is a click this pageview authored. Compared case-insensitively:
  // a URL id may be uppercase while the SDK answers canonical lowercase.
  const authoredHere =
    Boolean(transactionId) &&
    transactionId.toLowerCase() !== (urlTid || '').toLowerCase()

  if (authoredHere) {
    if (authType !== AUTH_TYPE_UNKNOWN) {
      markSub7Known(transactionId)
      return
    }
    resolveAuthTypeEventually()
      .then((lateAuthType) => {
        if (lateAuthType === AUTH_TYPE_UNKNOWN) return null
        return fireReplacementClick(
          buildTimeoutReplacementParams(params, organicDefaults),
          lateAuthType,
        )
      })
      .catch((err) => {
        console.warn('[everflow] late replacement failed:', err)
      })
    return
  }

  const candidateTid =
    urlTid || readCookie('ef_transaction_id') || readCookie('transaction_id')
  if (candidateTid) void repairForeignClick(candidateTid)
}

export const initEverflow = () => {
  if (typeof window === 'undefined' || !BASE_URL) return

  let urlParams
  try {
    urlParams = new URLSearchParams(window.location.search)
  } catch (e) {
    return
  }

  // Any id from an earlier pageview is stale — this one authors its own.
  clearReplacementTid()

  if (urlParams.get('clickid')) {
    clearEfCookies()
    return
  }

  // Snapshot BEFORE any await — react-router strips the query string later.
  const params = snapshotClickParams(urlParams)
  const organicDefaults = snapshotOrganicDefaults(urlParams)

  const scriptLoaded = new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = `${BASE_URL}/scripts/main.js`
    s.onload = resolve
    s.onerror = () => reject(new Error('everflow main.js failed to load'))
    document.head.appendChild(s)
  })

  // Runs in parallel with the GB wait, which is normally a warm cache hit and
  // settles inside the script's own fetch — so waiting usually costs nothing.
  Promise.all([scriptLoaded, resolveAuthTypeForClick()])
    .then(([, authType]) => {
      if (typeof window.EF === 'undefined') return

      if (ORGANIC_OFFER_ID && ORGANIC_PARTNER_ID) {
        window.EF.configure({
          organic: {
            offer_id: Number(ORGANIC_OFFER_ID),
            affiliate_id: Number(ORGANIC_PARTNER_ID),
            options: { sub7: authType },
          },
        })
      }

      // Sent only when present: an id puts EF into existing-click mode, so an
      // empty one must not reach it.
      const existingTransactionId = params.ef_transaction_id

      return Promise.resolve(
        window.EF.click({
          offer_id: params.cmpid,
          affiliate_id: params.pubid,
          source_id: params.source_id,
          ...pickSubs(params),
          sub7: authType,
          ...(existingTransactionId
            ? { transaction_id: existingTransactionId }
            : {}),
        }),
      )
        .catch((err) => {
          console.warn('[everflow] EF.click rejected:', err)
          return ''
        })
        .then((transactionId) => {
          if (transactionId) writeTransactionCookie(transactionId)
          followUpOnInitialClick(
            transactionId || '',
            authType,
            params,
            organicDefaults,
          )
        })
    })
    .catch((err) => {
      console.warn('[everflow] click skipped:', err)
    })
}

const readCookie = (name) => {
  if (typeof document === 'undefined') return null
  const re = new RegExp(`(?:^|;\\s*)${name}=([^;]+)`)
  const match = document.cookie.match(re)
  return match ? decodeURIComponent(match[1]) : null
}

export const getEfTransactionId = () => {
  // A replacement authored this visit is strictly newer than URL/cookie ids.
  try {
    const replacement = sessionStorage.getItem(REPLACEMENT_TID_KEY)
    if (replacement) return replacement
  } catch (e) {}

  let urlParams
  try {
    urlParams = new URLSearchParams(window.location.search)
  } catch (e) {
    return ''
  }

  const fromUrl = urlParams.get('ef_transaction_id')
  if (fromUrl) return fromUrl

  return readCookie('ef_transaction_id') || readCookie('transaction_id') || ''
}
